import { ImportImage } from "@pdfking/compatibility"
import { AutoThrow } from "@pdfking/errors"
import { UInt8Collect } from "@pdfking/io"
import type { Pixel } from "@pdfking/math"
import type { DrawnPageResult } from "@pdfking/pdfium"
import { Provision } from "@pdfking/pdfium"
import type { Media, PDFConvertFunction, PDFSplitResult } from "@pdfking/renderer-api"

const toSplitResult = ({
  bytes,
  colors,
  media,
  transparency,
}: DrawnPageResult): Required<PDFSplitResult> => ({
  stream: bytes,
  colors,
  transparency,
  ...(media as Media<Pixel>),
})

const rgbaToBgra = (data: Uint8Array): Uint8Array => {
  const out = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i += 4) {
    out[i] = data[i + 2]!
    out[i + 1] = data[i + 1]!
    out[i + 2] = data[i]!
    out[i + 3] = data[i + 3]!
  }
  return out
}

export const PDFConvert: PDFConvertFunction = async (input, resolution) => {
  for await (const result of Provision(async function* (api) {
    const bytes = await UInt8Collect(input.stream)

    // JPEG: embed directly
    if (bytes[0] === 0xff && bytes[1] === 0xd8) {
      yield toSplitResult(AutoThrow(await api.convert_jpeg(bytes, resolution)))
      return
    }

    // Other formats: decode in browser → BGRA bitmap
    const { data, width, height } = await ImportImage(bytes)
    const bmpResult = await api.convert_bitmap(rgbaToBgra(data), { width, height }, resolution)
    yield toSplitResult(AutoThrow(bmpResult))
  })) {
    return result
  }
  throw new Error("No result from Provision")
}
